import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Palette, Smartphone, Search, IndianRupee, LifeBuoy } from "lucide-react";
import { Counter } from "./effects/Counter";

const items = [
  { icon: MessageSquare, title: "Fast Communication", body: "Replies within hours, daily updates, no ghosting. You always know where your project stands." },
  { icon: Palette, title: "Modern Design", body: "Premium, conversion-focused interfaces inspired by world-class SaaS brands — not template clones." },
  { icon: Smartphone, title: "Mobile First Development", body: "60%+ of your traffic is on a phone. Every site is engineered to feel perfect there first." },
  { icon: Search, title: "SEO Optimised", body: "Clean code, fast load times, proper schema and on-page SEO so Google ranks you higher." },
  { icon: IndianRupee, title: "Affordable Pricing", body: "Transparent packages built for Indian businesses. Pay for outcomes, not jargon." },
  { icon: LifeBuoy, title: "Ongoing Support", body: "Post-launch maintenance, updates and growth advice. I stay invested in your results." },
];

export const WhyChoose = () => {
  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
              Why Me
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white">
              Why businesses choose <span className="gold-text italic">Divy Shetty.</span>
            </h2>
            <p className="mt-6 text-[#B0B0B0] max-w-md">
              You're not just hiring a freelancer — you're partnering with a developer
              who cares about your revenue as much as your design.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4">
              <div>
                <div className="font-display text-3xl text-white">
                  <Counter to={7} suffix="d" />
                </div>
                <p className="text-xs text-[#737373] mt-1">avg. delivery</p>
              </div>
              <div>
                <div className="font-display text-3xl text-white">
                  <Counter to={2} suffix="x" />
                </div>
                <p className="text-xs text-[#737373] mt-1">avg. inquiry lift</p>
              </div>
              <div>
                <div className="font-display text-3xl text-white">
                  <Counter to={100} suffix="%" />
                </div>
                <p className="text-xs text-[#737373] mt-1">code ownership</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {items.map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`why-card-${i}`}
                className="bg-[#0A0A0A] border border-[#222222] rounded-2xl p-6 transition-all hover:border-[#D4AF37]/30 hover:-translate-y-0.5"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg glass-gold flex items-center justify-center shrink-0">
                    <it.icon className="w-4.5 h-4.5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg text-white mb-2">{it.title}</h3>
                    <p className="text-sm text-[#B0B0B0] leading-relaxed">{it.body}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
